import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SEOHelmet from '../components/SEOHelmet';

const api = () => axios.create({
 baseURL: '/api',
 headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const EMPLOYER_REASONS = [
 { id: 'non_payment', label: ' Non-payment or late payment', desc: 'Employer didn\'t pay or paid late' },
 { id: 'unsafe_conditions', label: '⚠️ Unsafe work conditions', desc: 'Dangerous workplace, no safety equipment' },
 { id: 'harassment', label: ' Harassment or bullying', desc: 'Verbal, physical, or sexual harassment' },
 { id: 'discrimination', label: '⛔ Discrimination', desc: 'Based on race, gender, age, disability, etc.' },
 { id: 'false_listing', label: ' False or misleading listing', desc: 'Job was different from what was advertised' },
 { id: 'unprofessional', label: ' Unprofessional behaviour', desc: 'Rude, disrespectful, or unreasonable' },
 { id: 'other', label: ' Other', desc: 'Something else not listed above' },
];

const WORKER_REASONS = [
 { id: 'no_show', label: '❌ No-show', desc: 'Worker didn\'t turn up to the shift' },
 { id: 'unprofessional', label: ' Unprofessional behaviour', desc: 'Rude, disrespectful, or disruptive' },
 { id: 'harassment', label: ' Harassment', desc: 'Verbal, physical, or sexual harassment' },
 { id: 'fraud', label: ' Fraud or false credentials', desc: 'Fake certs, fake identity, or dishonesty' },
 { id: 'other', label: ' Other', desc: 'Something else not listed above' },
];

export default function ReportUserPage() {
 const [params] = useSearchParams();
 const navigate = useNavigate();
 const http = api();

 const reportType = params.get('type') || 'employer'; // 'employer' or 'worker'
 const userId = params.get('userId') || '';
 const userName = params.get('name') || '';

 const reasons = reportType === 'employer' ? EMPLOYER_REASONS : WORKER_REASONS;

 const [reason, setReason] = useState('');
 const [description, setDescription] = useState('');
 const [isAnonymous, setIsAnonymous] = useState(true);
 const [evidenceDesc, setEvidenceDesc] = useState('');
 const [evidenceType, setEvidenceType] = useState('screenshot');
 const [submitted, setSubmitted] = useState(false);
 const [submitting, setSubmitting] = useState(false);
 const [error, setError] = useState('');
 const [reportId, setReportId] = useState(null);
 const [evidenceSubmitted, setEvidenceSubmitted] = useState(false);

 const handleSubmit = async () => {
 if (!reason) { setError('Please select a reason'); return; }
 if (!description.trim()) { setError('Please describe what happened'); return; }
 setSubmitting(true); setError('');
 try {
 const res = await http.post('/reports', {
 reported_user_id: userId,
 reason,
 description,
 is_anonymous: isAnonymous
 });
 setReportId(res.data.report_id);
 setSubmitted(true);
 } catch (err) {
 setError(err.response?.data?.error || 'Failed to submit report');
 }
 setSubmitting(false);
 };

 const handleEvidence = async () => {
 if (!evidenceDesc.trim()) return;
 try {
 await http.post(`/reports/${reportId}/evidence`, {
 evidence_type: evidenceType,
 description: evidenceDesc
 });
 setEvidenceSubmitted(true);
 } catch (err) {
 setError(err.response?.data?.error || 'Failed to submit evidence');
 }
 };

 if (submitted) {
 return (
 <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
 <SEOHelmet title="Report Submitted" />
 <div className="max-w-md w-full">
 <div className="bg-white rounded-2xl p-8 border border-neutral-100 shadow-sm text-center">
 <div className="text-5xl mb-4">✅</div>
 <h1 className="text-xl font-bold text-neutral-900 mb-2">Report Submitted</h1>
 <p className="text-sm text-neutral-500 mb-6">
 Our team will review this within 24 hours.
 {isAnonymous && ' Your identity is kept completely confidential.'}
 </p>

 {/* Evidence submission */}
 {!evidenceSubmitted ? (
 <div className="text-left bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
 <h3 className="font-bold text-amber-800 text-sm mb-2"> Submit Proof (Optional)</h3>
 <p className="text-xs text-amber-700 mb-3">Evidence helps us take action faster. Describe any screenshots, photos, or messages you have.</p>
 <select value={evidenceType} onChange={e => setEvidenceType(e.target.value)}
 className="w-full mb-2 px-3 py-2 border border-amber-200 rounded-lg text-sm bg-white">
 <option value="screenshot"> Screenshot</option>
 <option value="photo"> Photo</option>
 <option value="text_log"> Text/Chat log</option>
 <option value="document"> Document</option>
 <option value="other"> Other</option>
 </select>
 <textarea value={evidenceDesc} onChange={e => setEvidenceDesc(e.target.value)}
 placeholder="Describe your evidence... (e.g. 'I have screenshots of the employer confirming the shift then denying payment')"
 className="w-full px-3 py-2 border border-amber-200 rounded-lg text-sm resize-none h-20 mb-2" />
 <button onClick={handleEvidence}
 className="w-full py-2 bg-amber-500 text-white font-bold rounded-lg text-sm hover:bg-amber-600 transition">
 Submit Evidence
 </button>
 </div>
 ) : (
 <div className="bg-brand-50 border border-brand-200 rounded-xl p-4 mb-6 text-sm text-brand-700 font-medium">
 ✅ Evidence submitted. Thank you.
 </div>
 )}

 <div className="flex gap-3">
 <button onClick={() => navigate(-1)} className="flex-1 py-2.5 border border-neutral-200 rounded-xl font-medium text-sm hover:bg-neutral-50">
 ← Go Back
 </button>
 <button onClick={() => navigate('/worker/dashboard')} className="flex-1 py-2.5 bg-brand-500 text-white rounded-xl font-bold text-sm hover:bg-brand-600">
 Dashboard
 </button>
 </div>
 </div>
 </div>
 </div>
 );
 }

 return (
 <div className="min-h-screen bg-neutral-50">
 <SEOHelmet title={`Report ${reportType === 'employer' ? 'Employer' : 'Worker'}`} />

 {/* Header */}
 <div className="bg-white border-b border-neutral-200 sticky top-0 z-10">
 <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
 <button onClick={() => navigate(-1)} className="p-2 hover:bg-neutral-100 rounded-lg text-neutral-500">←</button>
 <h1 className="font-bold text-neutral-900">Report {reportType === 'employer' ? 'Employer' : 'Worker'}</h1>
 </div>
 </div>

 <div className="max-w-lg mx-auto px-4 py-6">
 {/* Anonymous notice */}
 <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-5">
 <div className="flex items-start gap-3">
 <span className="text-xl"></span>
 <div>
 <p className="font-semibold text-blue-800 text-sm">Your report is anonymous by default</p>
 <p className="text-xs text-blue-600 mt-0.5">The reported user will never see who filed the report. Only ShiftMatch admins can view reporter details.</p>
 </div>
 </div>
 </div>

 {userName && (
 <div className="bg-white rounded-xl border border-neutral-100 p-4 mb-4 flex items-center gap-3">
 <div className="w-10 h-10 bg-neutral-100 rounded-full flex items-center justify-center text-lg">
 {reportType === 'employer' ? '' : ''}
 </div>
 <div>
 <p className="font-semibold text-neutral-900 text-sm">Reporting: {userName}</p>
 <p className="text-xs text-neutral-400">{reportType === 'employer' ? 'Employer' : 'Worker'}</p>
 </div>
 </div>
 )}

 {/* Reason selection */}
 <div className="mb-5">
 <h2 className="font-bold text-neutral-900 text-sm mb-3">What happened?</h2>
 <div className="space-y-2">
 {reasons.map(r => (
 <button key={r.id} onClick={() => { setReason(r.id); setError(''); }}
 className={`w-full text-left p-3.5 rounded-xl border transition ${
 reason === r.id
 ? 'border-brand-400 bg-brand-50 ring-1 ring-green-200'
 : 'border-neutral-100 bg-white hover:border-neutral-200'
 }`}>
 <p className="font-medium text-neutral-900 text-sm">{r.label}</p>
 <p className="text-xs text-neutral-500 mt-0.5">{r.desc}</p>
 </button>
 ))}
 </div>
 </div>

 {/* Description */}
 <div className="mb-5">
 <h2 className="font-bold text-neutral-900 text-sm mb-2">Tell us more</h2>
 <textarea value={description} onChange={e => setDescription(e.target.value)}
 placeholder="Describe what happened in detail. Include dates, times, and any relevant context..."
 className="w-full px-4 py-3 border border-neutral-200 rounded-xl text-sm resize-none h-32 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-100" />
 </div>

 {/* Anonymous toggle */}
 <div className="mb-6">
 <label className="flex items-center gap-3 p-3 bg-white rounded-xl border border-neutral-100 cursor-pointer">
 <input type="checkbox" checked={isAnonymous} onChange={e => setIsAnonymous(e.target.checked)}
 className="w-4 h-4 rounded text-brand-500 focus:ring-brand-400" />
 <div>
 <p className="font-medium text-neutral-900 text-sm">Keep my identity anonymous</p>
 <p className="text-xs text-neutral-400">Recommended. The reported user won't know who filed this.</p>
 </div>
 </label>
 </div>

 {/* 3 strikes notice */}
 <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-xl">
 <p className="text-xs text-red-700">
 <strong>⚡ 3-Strike Policy:</strong> Users with 3+ verified reports are automatically removed from ShiftMatch. 
 Critical reports (harassment, unsafe conditions, fraud) result in immediate strikes.
 </p>
 </div>

 {error && <p className="text-sm text-red-600 mb-3 font-medium">{error}</p>}

 <button onClick={handleSubmit} disabled={submitting}
 className="w-full py-3.5 bg-red-500 hover:bg-red-600 text-white font-bold rounded-xl text-sm transition disabled:opacity-50 active:scale-98">
 {submitting ? 'Submitting…' : ' Submit Report'}
 </button>

 <p className="text-center text-xs text-neutral-400 mt-3">
 False reports may result in action against your account.
 </p>
 </div>
 </div>
 );
}
